/**
 * MODULE: _shared/frontend-shell/src/features/crm/hooks/useCrmUnifiedTimeline.ts
 * PURPOSE: Fetch merged shell + RevOps timeline for account/contact/deal drawers (unified timeline)
 * DEPENDENCIES: @tanstack/react-query, @shared/lib/frontend/api/crmApi, ../types/activity.types
 * PUBLIC_API: useCrmUnifiedTimeline, mapTimelineItemToActivity
 * SIDE_EFFECTS: GET /api/v1/crm/timeline
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 72/150
 * RECENT: 2026-05-25 — unified timeline unified activity timeline.
 */

import { useQuery } from '@tanstack/react-query';
import { crmGetTimeline, type CrmTimelineItem } from '@shared/lib/frontend/api/crmApi';
import type { Activity, ActivityStatus, ActivityType } from '../types/activity.types';

export function mapTimelineItemToActivity(item: CrmTimelineItem): Activity {
  const status = (['open', 'scheduled', 'complete', 'overdue'].includes(item.status)
    ? item.status
    : item.status === 'completed'
      ? 'complete'
      : 'complete') as ActivityStatus;
  const type = (['call', 'email', 'meeting', 'note', 'task'].includes(item.type)
    ? item.type
    : 'note') as ActivityType;
  return {
    id: item.id,
    type,
    subject: item.subject,
    relatedEntityId: item.relatedEntityId,
    relatedEntityType:
      item.relatedEntityType === 'company'
        ? 'account'
        : (item.relatedEntityType as Activity['relatedEntityType']),
    relatedEntityName: item.relatedEntityName ?? '',
    ownerId: item.ownerId ?? '',
    ownerName: item.ownerName ?? '',
    dueAtISO: item.occurredAtISO,
    status,
    notes: item.notes ?? undefined,
    outcome: item.outcome ?? undefined,
    createdAtISO: item.createdAtISO,
    updatedAtISO: item.updatedAtISO,
    timelineSource: item.source,
    readOnly: item.readOnly ?? item.source === 'revops',
  };
}

export function useCrmUnifiedTimeline(
  entityId: string | null | undefined,
  entityKind: 'account' | 'contact' | 'deal',
  options?: { enabled?: boolean; limit?: number }
) {
  const enabled = Boolean(options?.enabled !== false && entityId);
  const query = useQuery({
    queryKey: ['crm', 'timeline', entityKind, entityId, options?.limit ?? 50],
    queryFn: async () => {
      const params =
        entityKind === 'account'
          ? { accountId: entityId!, limit: options?.limit }
          : entityKind === 'contact'
            ? { contactId: entityId!, limit: options?.limit }
            : { dealId: entityId!, limit: options?.limit };
      return crmGetTimeline(params);
    },
    enabled,
    staleTime: 30_000,
  });

  const activities = (query.data?.items ?? []).map(mapTimelineItemToActivity);

  return {
    activities,
    revopsLinked: query.data?.revopsLinked ?? false,
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
